import { useEffect, useState } from 'react';
import { api, formatMoney } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Scales, Gavel, Buildings, Clock, CheckCircle, Warning, Receipt, User } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const CLAIM_TYPE_COLORS = {
  financial: '#B4935A', eviction: '#DC2626', execution: '#7C3AED', judicial: '#1E3A5F', custom: '#64748B'
};

const KpiCard = ({ icon: Icon, label, value, color, onClick, testId }) => (
  <Card
    className={`p-5 border border-slate-200 rounded-md shadow-none ${onClick ? 'cursor-pointer hover:border-slate-900 hover:shadow-md transition-all' : ''}`}
    onClick={onClick}
    data-testid={testId}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs text-slate-500 mb-1">{label}</div>
        <div className="text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
      </div>
      <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${color}15`, color }}>
        <Icon size={20} weight="duotone" />
      </div>
    </div>
  </Card>
);

export default function LegalDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get('/legal/dashboard').then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="text-slate-400">جارٍ التحميل...</div>;
  if (!data) return <div className="text-slate-400">لا توجد بيانات</div>;

  const k = data.kpis;
  const goToClaims = (q) => nav(`/legal/claims?${new URLSearchParams(q).toString()}`);

  return (
    <div className="space-y-6" data-testid="legal-dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#1E3A5F] mb-1">Legal Command Center</div>
        <h1 className="text-3xl font-bold text-slate-900">لوحة الإدارة القانونية والقضائية</h1>
        <p className="text-sm text-slate-500 mt-1">نظرة تنفيذية على المطالبات والقضايا وأداء الفريق</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={Scales} label="إجمالي المطالبات" value={k.total_claims} color="#1E3A5F" onClick={() => goToClaims({})} testId="kpi-total-claims" />
        <KpiCard icon={Gavel} label="القضايا (لها رقم دعوى)" value={k.total_cases} color="#7C3AED" onClick={() => goToClaims({ has_case: '1' })} testId="kpi-total-cases" />
        <KpiCard icon={Clock} label="مفتوحة" value={k.open} color="#B4935A" onClick={() => goToClaims({ status: 'draft' })} testId="kpi-open" />
        <KpiCard icon={CheckCircle} label="مغلقة" value={k.closed} color="#059669" onClick={() => goToClaims({ status: 'closed' })} testId="kpi-closed" />
        <KpiCard icon={Warning} label="قيد النظر" value={k.in_hearing} color="#EA580C" onClick={() => goToClaims({ status: 'in_hearing' })} testId="kpi-in-hearing" />
        <KpiCard icon={Gavel} label="تحت التنفيذ" value={k.execution} color="#7C3AED" onClick={() => goToClaims({ status: 'execution' })} testId="kpi-execution" />
        <KpiCard icon={Buildings} label="قضايا إخلاء" value={k.eviction} color="#DC2626" onClick={() => goToClaims({ claim_type: 'eviction' })} testId="kpi-eviction" />
        <KpiCard icon={Receipt} label="إجمالي المطالب به" value={`${formatMoney(k.total_claimed)} ر.س`} color="#0F172A" testId="kpi-claimed-amount" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* By Employee */}
        <Card className="p-5 border border-slate-200 rounded-md shadow-none">
          <div className="flex items-center gap-2 mb-4">
            <User className="text-slate-500" size={18} />
            <h2 className="font-semibold text-slate-900">أداء الموظفين</h2>
          </div>
          {data.by_employee.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-6">لا توجد بيانات</div>
          ) : (
            <div className="space-y-2">
              {data.by_employee.map((e) => (
                <div key={e.user_id} className="flex items-center gap-3 p-2.5 rounded-md hover:bg-slate-50">
                  <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-xs font-semibold">
                    {(e.name || '?').charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{e.name}</div>
                    <div className="text-xs text-slate-500">{e.total} مطالبة · {e.open} مفتوحة · {e.closed} مغلقة</div>
                  </div>
                  <div className="text-sm font-semibold text-[#1E3A5F] tabular-nums">{formatMoney(e.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* By Branch */}
        <Card className="p-5 border border-slate-200 rounded-md shadow-none">
          <div className="flex items-center gap-2 mb-4">
            <Buildings className="text-slate-500" size={18} />
            <h2 className="font-semibold text-slate-900">توزيع القضايا حسب الفرع</h2>
          </div>
          {data.by_branch.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-6">لا توجد بيانات</div>
          ) : (
            <div className="space-y-2">
              {data.by_branch.map((b) => (
                <button
                  key={b.branch}
                  onClick={() => goToClaims({ branch: b.branch })}
                  className="w-full flex items-center gap-3 p-2.5 rounded-md hover:bg-slate-50 text-right"
                  data-testid={`branch-row-${b.branch}`}
                >
                  <div className="flex-1 text-sm text-slate-900">{b.branch}</div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#1E3A5F]" style={{ width: `${(b.count / Math.max(...data.by_branch.map(x => x.count))) * 100}%` }} />
                  </div>
                  <div className="text-sm font-semibold tabular-nums">{b.count}</div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* By Type */}
        <Card className="p-5 border border-slate-200 rounded-md shadow-none md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Scales className="text-slate-500" size={18} />
            <h2 className="font-semibold text-slate-900">توزيع المطالبات حسب النوع</h2>
          </div>
          {data.by_type.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-6">لا توجد بيانات</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {data.by_type.map((t) => (
                <button
                  key={t.type}
                  onClick={() => goToClaims({ claim_type: t.type })}
                  className="p-4 border border-slate-200 rounded-md hover:border-slate-900 hover:shadow-sm text-center transition-all"
                  data-testid={`type-tile-${t.type}`}
                >
                  <div className="text-3xl font-bold tabular-nums" style={{ color: CLAIM_TYPE_COLORS[t.type] || '#0F172A' }}>{t.count}</div>
                  <div className="text-xs text-slate-600 mt-1">{t.label_ar}</div>
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
